import React from "react";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import NorthIcon from "@mui/icons-material/North";
import SouthIcon from "@mui/icons-material/South";
import { generateGradeMessage } from "../../../../helpers/gradeMessage";
import { get_t_str } from "../../../../helpers/helpers";
import ScheduleBusinessScorecard, {
  buildLaborBusinessScorecardMetrics,
  calculateLaborGradeScore,
} from "./ScheduleBusinessScorecard";

const WEEKLY_GRADE_SCORECARD_KEYS = new Set(["coverage_score", "budget_score"]);
const WEEKLY_GRADE_SCORECARD_LABELS = {
  coverage_score: "Intraday Coverage",
};

export function GradeBadge({ color, label, interactive = false }) {
  const style = {
    fontSize: "14px",
    fontWeight: 700,
    color: "white",
    backgroundColor: color,
    borderRadius: "6px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "24px",
    height: "24px",
    border: 0,
    padding: 0,
    cursor: interactive ? "pointer" : "default",
  };

  return (
    <span
      style={style}
      className={interactive ? "hover:brightness-95" : "cursor-default"}
    >
      {label}
    </span>
  );
}

function GradeSummary({ summary }) {
  const lines = summary
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => line
      .replace(/^•\s*/, "")
      .replace(" hours over", " h over")
      .replace(" hrs at ", " h at "));

  if (!lines.length) return null;

  return (
    <div className="mt-4 rounded-md bg-slate-50 px-3 py-2.5">
      {lines.map((line, index) => (
        <p
          key={line}
          className={index === 0
            ? "text-xs font-medium leading-relaxed text-slate-900"
            : "mt-1 text-xs leading-relaxed text-slate-500"}
        >
          {line}
        </p>
      ))}
    </div>
  );
}

// Muted uppercase section label shared by the metrics sections.
function MetricEyebrow({ children }) {
  return (
    <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
      {children}
    </div>
  );
}

// Pill-style percent delta, matching the scorecard's DeltaBadge treatment.
function DeltaPill({ percent, caption = "vs forecast" }) {
  const value = Number(percent);
  if (percent === null || percent === undefined || isNaN(value)) return null;
  const isUp = value >= 0;
  return (
    <span
      className={`inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-xs font-semibold ${
        isUp ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"
      }`}
    >
      {isUp ? (
        <NorthIcon style={{ fontSize: "13px" }} />
      ) : (
        <SouthIcon style={{ fontSize: "13px" }} />
      )}
      {Math.abs(value)}%
      {caption && <span className="font-medium opacity-80">{caption}</span>}
    </span>
  );
}

function LaborComparison({ headings, rows, targetRows }) {
  return (
    <table className="mt-4 w-full text-[13px] tabular-nums" aria-label={`${headings.join(" versus ")} labor`}>
      <thead>
        <tr className="border-b border-slate-200 text-[10px] uppercase tracking-wide text-slate-500">
          <th scope="col" className="py-2 text-left font-semibold">Metric</th>
          {headings.map((heading) => <th key={heading} scope="col" className="py-2 pl-2 text-right font-semibold">{heading}</th>)}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, index) => (
          <tr key={row.label} className="border-b border-slate-100 last:border-0">
            <th scope="row" className="py-2 text-left font-normal text-slate-600">{row.label}</th>
            <td className="py-2 pl-2 text-right font-semibold text-slate-900">{row.value}</td>
            <td className="py-2 pl-2 text-right font-semibold text-slate-900">{targetRows[index].value}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function SalesBreakdown({ rows, actual = false }) {
  return (
    <table className="mt-4 w-full text-xs tabular-nums" aria-label="Sales breakdown">
      <thead>
        <tr className="border-b border-slate-200 text-slate-500">
          <th scope="col" className="pb-2 text-left font-semibold">Sales breakdown</th>
          <th scope="col" className="pb-2 pl-2 text-right font-semibold">{actual ? "Actual" : "Forecast"}</th>
          {actual && <th scope="col" className="pb-2 pl-3 text-right font-semibold whitespace-nowrap">vs forecast</th>}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.label} className="odd:bg-slate-50">
            <th scope="row" className="py-2 pl-1.5 text-left font-normal text-slate-700">{row.label}</th>
            <td className="py-2 px-2 text-right font-semibold text-slate-900">{row.value}</td>
            {actual && <td className="py-2 pr-1.5 pl-3 text-right">
              <span className={`inline-flex items-center gap-0.5 whitespace-nowrap ${row.delta === 0 ? "text-slate-500" : row.delta > 0 ? "text-emerald-700" : "text-rose-700"}`}>
                {row.delta > 0 && <NorthIcon style={{ fontSize: 13 }} />}
                {row.delta < 0 && <SouthIcon style={{ fontSize: 13 }} />}
                {Math.abs(row.delta)}%
              </span>
            </td>}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function DailyMetricsCard2(props) {
  // Event handlers for mouse enter and leave events on stats tooltip - no longer needed
  // const handleMouseEnterStats = () => {
  //   setShowTooltipStats(true);
  // };

  // const handleMouseLeaveStats = () => {
  //   setShowTooltipStats(false);
  // };

  // Calculate the color for the planned grade based on schedule and forecasted labor
  const planned_letter_color = props.calculate_color(
    props.actual_schedule_labor,
    props.forecasted_labor
  );

  // Calculate the color for the actual grade based on used labor and earned labor
  const actual_letter_color = props.calculate_color(
    props.actual_used_labor_vs_earned,
    props.calculated_earned_labor
  );
  const plannedGradeScore = React.useMemo(
    () =>
      calculateLaborGradeScore({
        laborRows: props.actual_schedule_labor,
        targetLaborRows: props.forecasted_labor,
        gradeWeights: props.grade_weights,
        minimumTargetHours: 10,
      }),
    [
      props.actual_schedule_labor,
      props.forecasted_labor,
      props.grade_weights,
    ]
  );
  const actualGradeScore = React.useMemo(
    () =>
      calculateLaborGradeScore({
        laborRows: props.actual_used_labor_vs_earned,
        targetLaborRows: props.calculated_earned_labor,
        gradeWeights: props.grade_weights,
        minimumTargetHours: 10,
      }),
    [
      props.actual_used_labor_vs_earned,
      props.calculated_earned_labor,
      props.grade_weights,
    ]
  );
  // Tooltip messages explaining grade drivers
  const plannedGradeTooltip = React.useMemo(
    () =>
      generateGradeMessage(
        props.actual_schedule_labor,
        props.forecasted_labor,
        get_t_str
      ),
    [props.actual_schedule_labor, props.forecasted_labor]
  );

  const actualGradeTooltip = React.useMemo(
    () =>
      generateGradeMessage(
        props.actual_used_labor_vs_earned,
        props.calculated_earned_labor,
        get_t_str
      ),
    [props.actual_used_labor_vs_earned, props.calculated_earned_labor]
  );

  const plannedBusinessScorecardMetrics = React.useMemo(
    () =>
      buildLaborBusinessScorecardMetrics({
        laborRows: props.actual_schedule_labor,
        targetLaborRows: props.forecasted_labor,
        scheduleRows: props.actual_schedule,
        openShiftRows: props.open_shifts,
        gradeWeights: props.grade_weights,
        minimumTargetHours: 10,
      }).filter((metric) => WEEKLY_GRADE_SCORECARD_KEYS.has(metric.key)),
    [
      props.actual_schedule_labor,
      props.forecasted_labor,
      props.actual_schedule,
      props.open_shifts,
      props.grade_weights,
    ]
  );

  const actualBusinessScorecardMetrics = React.useMemo(
    () =>
      buildLaborBusinessScorecardMetrics({
        laborRows: props.actual_used_labor_vs_earned,
        targetLaborRows: props.calculated_earned_labor,
        gradeWeights: props.grade_weights,
        minimumTargetHours: 10,
      }).filter((metric) => WEEKLY_GRADE_SCORECARD_KEYS.has(metric.key)),
    [
      props.actual_used_labor_vs_earned,
      props.calculated_earned_labor,
      props.grade_weights,
    ]
  );

  const plannedGrade = {
    letter: props.calculate_letter(
      props.actual_schedule_labor,
      props.forecasted_labor
    ),
    score: plannedGradeScore,
    color: planned_letter_color,
  };

  const actualGrade = {
    letter: props.calculate_letter(
      props.actual_used_labor_vs_earned,
      props.calculated_earned_labor
    ),
    score: actualGradeScore,
    color: actual_letter_color,
  };

  const plannedCanOpenGradeScorecard =
    plannedGrade.letter !== "-" &&
    plannedGrade.color !== "gray" &&
    plannedGrade.score !== null &&
    plannedGradeTooltip.trim().length > 0 &&
    plannedBusinessScorecardMetrics.length > 0;

  const actualCanOpenGradeScorecard =
    actualGrade.letter !== "-" &&
    actualGrade.color !== "gray" &&
    actualGrade.score !== null &&
    actualGradeTooltip.trim().length > 0 &&
    actualBusinessScorecardMetrics.length > 0;

  const plannedGradeScorecardContent = (
    <>
      <ScheduleBusinessScorecard
        title="Planned Scorecard"
        subtitle="Scheduled versus forecasted"
        metrics={plannedBusinessScorecardMetrics}
        grade={plannedGrade}
        showValueLabels={false}
        metricLabelOverrides={WEEKLY_GRADE_SCORECARD_LABELS}
        compact
      />
      <GradeSummary summary={plannedGradeTooltip} />
    </>
  );

  const actualGradeScorecardContent = (
    <>
      <ScheduleBusinessScorecard
        title="Actual Scorecard"
        subtitle="Actual labor versus earned labor."
        metrics={actualBusinessScorecardMetrics}
        grade={actualGrade}
        showValueLabels={false}
        metricLabelOverrides={WEEKLY_GRADE_SCORECARD_LABELS}
        compact
      />
      <GradeSummary summary={actualGradeTooltip} />
    </>
  );

  // const show_actuals = !props.edit_mode && props.day_in_past;
  const show_actuals = props.checkedMetricsToggle;
  const canShowActuals = props.day_in_past || props.todays_date_yes;

  // Render the +/- metric value based on actual and recommended values
  function showPlusMinus(actual, recommended) {
    if (recommended === 0 || actual === 0) {
      return (
        <div className="text-xxs w-[65px] py-1 rounded flex items-center justify-center gap-0.5">
          <p></p>
        </div>
      );
    }

    // Calculate the difference between actual and recommended values
    const value = actual - recommended;
    if (value <= 0) {
      return (
        <div className="bg-emerald-200 text-emerald-800 text-xxs px-1.5 py-1 rounded flex items-center justify-center gap-0.5">
          <p>{-1 * value} hrs</p>
          <ArrowDownwardIcon style={{ fontSize: "12px" }} />
        </div>
      );
    } else {
      return (
        <div className="bg-rose-200 text-rose-800 text-xxs px-1.5 py-1 rounded flex items-center justify-center gap-0.5">
          <p>{value} hrs</p>
          <ArrowUpwardIcon style={{ fontSize: "12px" }} />
        </div>
      );
    }
  }

  const [showActualSalesBreakdown, setShowActualSalesBreakdown] =
    React.useState(false);

  function toggleShowActualSalesBreakdown() {
    setShowActualSalesBreakdown((prevState) => !prevState);
  }

  const [showPlannedSalesBreakdown, setShowPlannedSalesBreakdown] =
    React.useState(false);

  function toggleShowPlannedSalesBreakdown() {
    setShowPlannedSalesBreakdown((prevState) => !prevState);
  }

  const isLastCard = props.isLastCard;

  function calculatePercentageDifference(actual, forecast) {
    if (forecast === 0) {
      return 0;
    }
    return ((actual - forecast) / forecast) * 100;
  }

  // Calculate the percent difference
  const calculatePercentDifference = (actual, forecasted) => {
    const difference = actual - forecasted;
    const average = (actual + forecasted) / 2;
    const relativeDifference = difference / average;
    const percentDifference = relativeDifference * 100;

    return percentDifference;
  };

  const salesDifference = calculatePercentDifference(
    props.actual_sales,
    props.forecast_sales
  ).toFixed(0);
  const salesDifferenceWeekly = calculatePercentDifference(
    props.actual_sales,
    props.forecast_sales_up_to_current_day
  ).toFixed(0);

  // Actual labor percent calculation
  let totalDayLaborCostActual =
    props.agg_column(props.dailyLaborPercentData, "amount_cents") / 100;
  const averageWageActual =
    totalDayLaborCostActual /
    props.agg_column(props.dailyLaborPercentData, "hours");
  // totalDayLaborCostActual =
  //   props.agg_column(props.actual_used_labor_vs_earned, "hours") *
  //   averageWageActual;
  const actualLaborPercent =
    (totalDayLaborCostActual / props.actual_sales) * 100;

  // Earned labor percent calculation
  const earnedHours = props.agg_column(props.calculated_earned_labor, "hours");
  const totalDayLaborCostEarned = earnedHours * averageWageActual;
  const earnedLaborPercent =
    (totalDayLaborCostEarned / props.actual_sales) * 100;

  // Scheduled labor percent calculation
  const scheduledHours = props.agg_column(props.actual_schedule_labor, "hours");
  const totalDayLaborCostScheduled =
    scheduledHours * props.averageWageFromPreviousWeek;
  const scheduledLaborPercent =
    (totalDayLaborCostScheduled / props.forecast_sales) * 100;

  // Forecasted labor percent calculation
  const forecastedHours = props.agg_column(props.forecasted_labor, "hours");
  const totalDayLaborCostForecasted =
    forecastedHours * props.averageWageFromPreviousWeek;
  const forecastedLaborPercent =
    (totalDayLaborCostForecasted / props.forecast_sales) * 100;

  const forecastedTransactions = props.daily_forecast_other;

  // Build the aligned metric rows for a labor group, honoring the
  // showLaborPercent (rich) vs compact display modes.
  function buildLaborRows({
    hours,
    laborPercent,
    splhSales,
    splhTransactions,
    totalCost,
  }) {
    if (props.showLaborPercent) {
      return [
        {
          label: "Labor Percent",
          value: isNaN(laborPercent)
            ? "0.00%"
            : `${laborPercent.toFixed(2)}%`,
        },
        {
          label: "SPLH",
          value: `$${hours === 0 ? 0 : (splhSales / hours).toFixed()}`,
        },
        {
          label: "Total Cost",
          value: isNaN(totalCost)
            ? "$0"
            : `$${totalCost.toLocaleString(undefined, {
                maximumFractionDigits: 0,
              })}`,
        },
        { label: "Hours", value: `${hours.toFixed(2)} h` },
      ];
    }
    return [
      { label: "Hours", value: `${hours.toFixed(2)} h` },
      {
        label: props.tplhEnabled ? "TPLH" : "SPLH",
        value: `${props.tplhEnabled ? "" : "$"}${
          hours === 0
            ? 0
            : (
                (props.tplhEnabled ? splhTransactions : splhSales) / hours
              ).toFixed(props.tplhEnabled ? 2 : 0)
        }`,
      },
    ];
  }

  const scheduledLaborRows = buildLaborRows({
    hours: props.agg_column(props.actual_schedule_labor, "hours"),
    laborPercent: scheduledLaborPercent,
    splhSales: props.forecast_sales,
    splhTransactions: props.forecast_transactions,
    totalCost: totalDayLaborCostScheduled,
  });
  const forecastedLaborRows = buildLaborRows({
    hours: props.agg_column(props.forecasted_labor, "hours"),
    laborPercent: forecastedLaborPercent,
    splhSales: props.forecast_sales,
    splhTransactions: props.forecast_transactions,
    totalCost: totalDayLaborCostForecasted,
  });
  const usedLaborRows = buildLaborRows({
    hours: props.agg_column(props.actual_used_labor_vs_earned, "hours"),
    laborPercent: actualLaborPercent,
    splhSales: props.actual_sales,
    splhTransactions: props.actual_transactions,
    totalCost: totalDayLaborCostActual,
  });
  const earnedLaborRows = buildLaborRows({
    hours: props.agg_column(props.calculated_earned_labor, "hours"),
    laborPercent: earnedLaborPercent,
    splhSales: props.actual_sales,
    splhTransactions: props.actual_transactions,
    totalCost: totalDayLaborCostEarned,
  });

  const activeSalesDifference =
    props.weekly_or_daily === "weekly" ? salesDifferenceWeekly : salesDifference;

  // Create planned metrics tooltip content
  const plannedMetricsTooltipContent = (
    <div>
      <h4 className="text-slate-900 font-bold text-sm">Planned Metrics</h4>

      <div className="mt-4">
        <div className="flex items-center justify-between">
          <MetricEyebrow>Sales</MetricEyebrow>
          {props.daily_forecast_other?.length > 0 && (
            <button
              type="button"
              onClick={toggleShowPlannedSalesBreakdown}
              aria-expanded={showPlannedSalesBreakdown}
              aria-label={
                showPlannedSalesBreakdown
                  ? "Hide sales breakdown"
                  : "Show sales breakdown"
              }
              className="inline-flex items-center justify-center border-0 bg-transparent p-0 text-slate-400 hover:text-slate-700 cursor-pointer rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-violet-500"
            >
              {showPlannedSalesBreakdown ? (
                <ExpandLessIcon style={{ fontSize: "20px" }} />
              ) : (
                <ExpandMoreIcon style={{ fontSize: "20px" }} />
              )}
            </button>
          )}
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-2.5">
          <p className="text-xl font-semibold text-slate-900 tabular-nums">
            ${Math.round(props.forecast_sales).toLocaleString()}
          </p>
        </div>
        {showPlannedSalesBreakdown && (
          <SalesBreakdown rows={(props.daily_forecast_other || []).map((row) => ({
            label: row.metric,
            value: Math.round(row.value * props.daily_adjustment),
          }))} />
        )}
      </div>
      <LaborComparison headings={["Scheduled", "Forecasted"]} rows={scheduledLaborRows} targetRows={forecastedLaborRows} />
    </div>
  );

  // Create actual metrics tooltip content
  const actualMetricsTooltipContent = (
    <div>
      <h4 className="text-slate-900 font-bold text-sm">Actual Metrics</h4>

      <div className="mt-4">
        <div className="flex items-center justify-between">
          <MetricEyebrow>Sales</MetricEyebrow>
          {props.daily_actual_other?.length > 0 &&
            props.daily_forecast_other?.length > 0 && (
              <button
                type="button"
                onClick={toggleShowActualSalesBreakdown}
                aria-expanded={showActualSalesBreakdown}
                aria-label={
                  showActualSalesBreakdown
                    ? "Hide sales breakdown"
                    : "Show sales breakdown"
                }
                className="inline-flex items-center justify-center border-0 bg-transparent p-0 text-slate-400 hover:text-slate-700 cursor-pointer rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-violet-500"
              >
                {showActualSalesBreakdown ? (
                  <ExpandLessIcon style={{ fontSize: "20px" }} />
                ) : (
                  <ExpandMoreIcon style={{ fontSize: "20px" }} />
                )}
              </button>
            )}
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-2.5">
          <p className="text-xl font-semibold text-slate-900 tabular-nums">
            ${Math.round(props.actual_sales).toLocaleString()}
          </p>
          <DeltaPill percent={activeSalesDifference} />
        </div>
        {showActualSalesBreakdown && (
          <SalesBreakdown actual rows={(props.daily_actual_other || []).map((row) => {
            const forecast = props.daily_forecast_other?.find((item) => item.metric === row.metric);
            return {
              label: row.metric,
              value: row.metric === "Tips" ? (row.value / 100).toFixed(2) : Math.round(row.value),
              delta: Math.round(forecast ? calculatePercentageDifference(row.value, forecast.value * props.daily_adjustment) : 0),
            };
          })} />
        )}
      </div>
      <LaborComparison headings={["Used", "Earned"]} rows={usedLaborRows} targetRows={earnedLaborRows} />
    </div>
  );

  // Merge the grade scorecard (previously a click-triggered popover) into the
  // hover tooltip so all detail lives on a single, discoverable surface.
  const plannedTooltipContent = (
    <div>
      {plannedCanOpenGradeScorecard && (
        <div className="mb-5">
          {plannedGradeScorecardContent}
          <div className="mt-5">
            <Divider style={{ backgroundColor: "#e2e8f0" }} />
          </div>
        </div>
      )}
      {plannedMetricsTooltipContent}
    </div>
  );

  const actualTooltipContent = (
    <div>
      {actualCanOpenGradeScorecard && (
        <div className="mb-5">
          {actualGradeScorecardContent}
          <div className="mt-5">
            <Divider style={{ backgroundColor: "#e2e8f0" }} />
          </div>
        </div>
      )}
      {actualMetricsTooltipContent}
    </div>
  );

  const tooltipSlotProps = {
    tooltip: {
      sx: {
        backgroundColor: "white",
        padding: "0px",
        borderRadius: "12px",
        boxShadow:
          "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
        border: "1px solid #e2e8f0",
        color: "inherit",
        width: "440px",
        maxWidth: "calc(100vw - 24px)",
      },
    },
    arrow: { sx: { color: "white" } },
  };

  const renderUnavailable = (used = null, target = null) => (
    <div className="w-full flex flex-col gap-1" aria-label={`${props.day_of_week_str || "Week"} metrics unavailable`}>
      <span className="text-xs font-bold text-slate-500">{props.day_of_week_str || "Week"}</span>
      {Number.isFinite(used) && <p className="text-xs text-slate-700 tabular-nums"><span className="font-semibold">{used.toFixed(2)}</span> h {show_actuals ? "used" : "scheduled"}</p>}
      {Number.isFinite(target) && <p className="text-xs text-slate-500 tabular-nums">{target.toFixed(2)} h {show_actuals ? "earned" : "forecasted"}</p>}
      <p className="text-xxs text-slate-400">No data yet</p>
    </div>
  );

  // Compact four-line card: label + grade, hours, bar, percent + gap. The
  // weekly card uses the same lines so its bar sits level with the day bars.
  const renderCard = ({ used, target, grade, canOpen, ariaLabel, content }) => {
    const hasValues = Number.isFinite(used) && Number.isFinite(target);
    if (!hasValues) return renderUnavailable(used, target);
    const ratio = hasValues && target > 0 ? (used / target) * 100 : null;
    const formatHours = (value, precision) => value.toFixed(precision);
    if (ratio === null) {
      grade = null;
      canOpen = false;
    }
    const over = hasValues && used > target;
    const gap = Math.abs(used - target);
    const decimals = props.windowWidth <= 1279 ? 0 : 2;
    // Keep the primary metrics and bar neutral. The grade badge and the
    // explicit over-budget variance provide enough status emphasis.
    const fill = "#1e293b";
    const label = props.day_of_week_str ? props.day_of_week_str : "Week";
    return (
      <Tooltip
        title={
          content ? <div
            style={{
              maxHeight: "78vh",
              overflowY: "auto",
              padding: "20px",
              borderRadius: "12px",
            }}
          >
            {content}
          </div> : ""
        }
        arrow
        placement="top"
        PopperProps={{ modifiers: [{ name: "preventOverflow", options: { padding: 12 } }] }}
        componentsProps={tooltipSlotProps}
      >
        {props.variant === "header" ? (
          <div
            tabIndex={canOpen ? 0 : undefined}
            role={canOpen ? "button" : undefined}
            aria-label={canOpen ? ariaLabel : undefined}
            className="w-full flex items-center justify-between gap-2 leading-none rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2"
          >
            <span className="text-xs font-bold text-slate-500">
              {label}
            </span>
            <div className="flex items-center gap-2">
              <p className="text-xxs tabular-nums">
                <span className="font-semibold text-slate-700">
                  {formatHours(used, 2)}
                </span>
                <span className="text-slate-500"> / {formatHours(target, 2)}</span>
              </p>
              {grade && grade.letter !== "-" && grade.letter !== "—" && <GradeBadge color={grade.color} label={grade.letter} />}
            </div>
          </div>
        ) : (
        <div
          tabIndex={canOpen ? 0 : undefined}
          role={canOpen ? "button" : undefined}
          aria-label={canOpen ? ariaLabel : undefined}
          className="w-full flex flex-col gap-1 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2"
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-500">
              {label}
            </span>
            {grade && grade.letter !== "-" && grade.letter !== "—" && <GradeBadge color={grade.color} label={grade.letter} />}
          </div>
          <div className="flex items-baseline gap-1 tabular-nums">
            <span className="text-sm font-semibold leading-none text-slate-700">
              {formatHours(used, decimals)}
            </span>
            <span className="text-xs font-medium text-slate-500">
              / {formatHours(target, decimals)} h
            </span>
          </div>
          <div className="w-full h-1.5 bg-slate-200 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full"
              style={{
                width: `${Math.max(0, Math.min(ratio ?? 0, 100)).toFixed(1)}%`,
                backgroundColor: fill,
              }}
            ></div>
          </div>
          <div className="flex items-center justify-between text-xxs tabular-nums">
            <span className="font-semibold text-slate-700">
              {ratio === null ? "" : `${ratio.toFixed(1)}%`}
            </span>
            {props.windowWidth > 1279 && (
              <span
                className={over ? "text-rose-700 font-semibold" : "text-slate-500"}
              >
                {`${gap.toFixed(2)} h ${over ? "over" : "under"}`}
              </span>
            )}
          </div>
        </div>
        )}
      </Tooltip>
    );
  };

  if (show_actuals && !canShowActuals) return renderUnavailable();
  if (props.targetLaborAvailable === false) {
    return renderUnavailable(props.fallbackUsedHours);
  }

  if (!show_actuals) {
    return renderCard({
      used: props.agg_column(props.actual_schedule_labor, "hours"),
      target: props.agg_column(props.forecasted_labor, "hours"),
      grade: plannedGrade,
      canOpen: plannedCanOpenGradeScorecard,
      ariaLabel: "Show planned scorecard and metrics",
      content: plannedTooltipContent,
    });
  }

  const hasActualLaborRows = props.actual_used_labor_vs_earned?.length > 0;
  const canGradeActual = hasActualLaborRows && props.actualGradeAvailable !== false;
  if (props.actualComparisonAvailable === false) {
    return renderUnavailable(
      props.actualUsedHours ?? props.fallbackUsedHours,
      props.agg_column(props.calculated_earned_labor, "hours")
    );
  }
  return renderCard({
    used: props.actualUsedHours ?? (hasActualLaborRows ? props.agg_column(props.actual_used_labor_vs_earned, "hours") : (props.fallbackUsedHours ?? null)),
    target: props.agg_column(props.calculated_earned_labor, "hours"),
    grade: canGradeActual ? actualGrade : null,
    canOpen: canGradeActual && actualCanOpenGradeScorecard,
    ariaLabel: "Show actual scorecard and metrics",
    content: canGradeActual ? actualTooltipContent : null,
  });
}

export default DailyMetricsCard2;
